import {
  EmailRounded,
  LockRounded,
  PinRounded,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Logo from "../components/logo";
import { ViewModelRecoverPassword } from "../viewmodel/viewmodel-recover-password";

const STEPS = ["Email", "Código", "Nova senha"];

const PENDING_TITLE = {
  title: "Confirmar conta",
  subtitle: "Insira o código enviado para o seu email",
};

const TITLES = [
  {
    title: "Recuperar senha",
    subtitle: "Insira o email da sua conta para receber um código",
  },
  {
    title: "Código de confirmação",
    subtitle: "Insira o código enviado para o seu email",
  },
  {
    title: "Nova senha",
    subtitle: "Defina a nova senha (6 a 15 dígitos)",
  },
];

function RecoverPasswordPage() {
  const vm = ViewModelRecoverPassword();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (vm.loading) return;
    if (vm.step === 0) vm.sendCode(email);
    else if (vm.step === 1) vm.confirmCode(code);
    else vm.changePassword(password, confirm);
  };

  const { title, subtitle } = vm.pending && vm.step === 1 ? PENDING_TITLE : TITLES[vm.step];

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#fff", p: 2 }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 400,
          p: { xs: 3, sm: 4 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Logo size={56} />
          </Box>
          <Typography variant="h5" component="h1">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>

        <Stepper activeStep={vm.step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {vm.step === 0 && (
            <TextField
              label="Email"
              type="email"
              fullWidth
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputProps={{ inputMode: "email" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {vm.step === 1 && (
            <>
              <TextField
                label="Código de confirmação"
                fullWidth
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                autoComplete="one-time-code"
                inputProps={{ maxLength: vm.CODE_LENGTH }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PinRounded fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button color="inherit" sx={{ color: "text.secondary" }} onClick={vm.resend} disabled={vm.loading}>
                Reenviar código
              </Button>
            </>
          )}

          {vm.step === 2 && (
            <>
              <TextField
                label="Nova senha"
                type={showPassword ? "text" : "password"}
                fullWidth
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirmar nova senha"
                type={showPassword ? "text" : "password"}
                fullWidth
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {vm.dialog.message && (
            <Alert severity={vm.dialog.type === "error" ? "error" : "success"}>{vm.dialog.message}</Alert>
          )}

          <Button type="submit" variant="contained" size="large" fullWidth disabled={vm.loading}>
            {vm.loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : vm.step === 0 ? (
              "Enviar código"
            ) : vm.step === 1 ? (
              "Confirmar código"
            ) : (
              "Alterar senha"
            )}
          </Button>

          {vm.step > vm.firstStep && (
            <Button color="inherit" sx={{ color: "text.secondary" }} onClick={vm.back} disabled={vm.loading}>
              Voltar
            </Button>
          )}

          <Button component={RouterLink} to="/login" color="inherit" sx={{ color: "text.secondary" }}>
            ← Voltar ao login
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default RecoverPasswordPage;
